import React from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import useInventoryStore from '../stores/inventoryStore';
import { useTranslation } from 'react-i18next';
import CurrencyIcon from './CurrencyIcon';
import useScrollToTop from '../hooks/useScrollToTop';

const Container = styled.div`
  min-height: 100vh;
  background: ${props => props.theme.background};
  padding: 0 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.theme.text.primary};
  text-align: center;
  margin: 0 0 16px 0;
`;

const Tabs = styled.div`
  display: flex;
  justify-content: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const TabButton = styled.button`
  padding: 8px 20px;
  border-radius: 20px;
  border: none;
  background: ${({ active, theme }) => active ? theme.primary : theme.card.background};
  color: ${({ active, theme }) => active ? '#fff' : theme.text.primary};
  font-weight: 600;
  font-size: 15px;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  box-shadow: ${({ active }) => active ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'};
`;

const Content = styled.div`
  padding: 0 0 calc(var(--tg-safe-area-inset-bottom, 0px) + 62px) 0;
`;

const SkinsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
  padding: 0 0 16px 0;
`;

const SkinCard = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s, border 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  position: relative;
  border: ${({ $error }) => $error ? '2px solid #ff4444' : '2px solid transparent'};
  box-sizing: border-box;

  &:active {
    transform: scale(0.98);
  }
`;

const SkinImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: contain;
  padding: 12px;
  background: rgba(0, 0, 0, 0.2);
  background-image: url('/skin_bg.png');
  background-size: 120%;
  background-position: center 60%;
`;

const SkinInfo = styled.div`
  padding: 12px;
`;

const SkinName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ $error, theme }) => $error ? '#ff4444' : theme.text.primary};
  margin-bottom: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SkinPrice = styled.div`
  font-size: 12px;
  color: ${props => props.theme.text.secondary};
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0 0 0;
  text-align: center;
  color: ${props => props.theme.text.secondary};
`;

const PendingNotification = styled.div`
  background: ${props => props.theme.card.background};
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  border-left: 4px solid #ffa500;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
`;

const NotificationTitle = styled.div`
  font-weight: 600;
  color: ${props => props.theme.text.primary};
  margin-bottom: 8px;
  font-size: 16px;
`;

const NotificationText = styled.div`
  color: ${props => props.theme.text.secondary};
  font-size: 14px;
  line-height: 1.4;
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

const HoldIndicator = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  z-index: 2;
`;

const TABS = [
  { key: 'received', label: 'skins.tabReceived' },
  { key: 'pending', label: 'skins.tabPending' },
  { key: 'sent', label: 'skins.tabSent' },
];

const MySkins = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const inventory = useInventoryStore(state => state.items);
  const fetchItems = useInventoryStore(state => state.fetchItems);
  const isLoading = useInventoryStore(state => state.isLoading);
  const [activeTab, setActiveTab] = React.useState('received');
  useScrollToTop([activeTab]);

  React.useEffect(() => {
    fetchItems();
  }, []);

  // React.useEffect(() => {
  //   if (WebApp.platform !== 'unknown') {
  //     WebApp.BackButton.show();
  //     if (WebApp.BackButton.setParams) {
  //       WebApp.BackButton.setParams({ is_visible: true, text: 'Закрыть' });
  //     }
  //     WebApp.BackButton.onClick(() => {
  //       if (WebApp.close) WebApp.close();
  //     });
  //   }
  //   return () => {
  //     if (WebApp.platform !== 'unknown') {
  //       WebApp.BackButton.hide();
  //       WebApp.BackButton.offClick();
  //     }
  //   };
  // }, []);

  const handleTabChange = (tabKey) => {
    fetchItems(); // Стор сам проверит, нужно ли делать запрос
    setActiveTab(tabKey);
  };

  const handleSkinClick = (skin) => {
    console.log('skin', skin);
    navigate(`/skin/${skin.id}`, { state: { weapon: skin } });
  };

  // Фильтрация по табам
  let filteredSkins = [];
  if (activeTab === 'received') {
    filteredSkins = inventory.filter(w => w.status !== 'sent' && w.status !== 'pending' && w.status !== 'hold');
  } else if (activeTab === 'pending') {
    filteredSkins = inventory.filter(w => w.status === 'pending');
  } else if (activeTab === 'sent') {
    filteredSkins = inventory.filter(w => w.status === 'sent' || w.status === 'hold');
  }

  return (
    <Container>
      <Title>{t('skins.mySkins')}</Title>
      <Tabs>
        {TABS.map(tab => (
          <TabButton
            key={tab.key}
            active={activeTab === tab.key}
            onClick={() => handleTabChange(tab.key)}
          >
            {t(tab.label)}
          </TabButton>
        ))}
      </Tabs>
      <Content>
        {/* Уведомление для таба pending */}
        {activeTab === 'pending' && filteredSkins.length > 0 && (
          <PendingNotification>
            <NotificationTitle>{t('skins.pendingNotificationTitle')}</NotificationTitle>
            <NotificationText>{t('skins.pendingNotificationText')}</NotificationText>
          </PendingNotification>
        )}

        {isLoading ? (
          <SkinsGrid>
            {Array.from({ length: 6 }).map((_, i) => (
              <SkinCard key={i} style={{ opacity: 0.5 }}>
                <div style={{ width: '100%', height: 120, background: 'rgba(200,200,200,0.12)', borderRadius: 12, marginBottom: 8 }} />
                <div style={{ width: '60%', height: 18, background: 'rgba(200,200,200,0.12)', borderRadius: 8, margin: '0 auto 8px auto' }} />
                <div style={{ width: 48, height: 18, background: 'rgba(200,200,200,0.12)', borderRadius: 8, margin: '0 auto' }} />
              </SkinCard>
            ))}
          </SkinsGrid>
        ) : filteredSkins.length === 0 ? (
          <EmptyState>
            <EmptyIcon>🎮</EmptyIcon>
            {activeTab === 'received' && <>
              <div>{t('skins.emptyReceived')}</div>
              <div style={{ color: '#888', marginTop: 8 }}>{t('skins.emptyReceivedSub')}</div>
            </>}
            {activeTab === 'pending' && <>
              <div>{t('skins.emptyPending')}</div>
              <div style={{ color: '#888', marginTop: 8 }}>{t('skins.emptyPendingSub')}</div>
            </>}
            {activeTab === 'sent' && <>
              <div>{t('skins.emptySent')}</div>
            </>}
          </EmptyState>
        ) : (
          <SkinsGrid>
            {filteredSkins.map((skin) => (
              <SkinCard key={skin.id} onClick={() => handleSkinClick(skin)} $error={skin.status === 'error'}>
                <SkinImage src={skin.iconUrl} alt={skin.marketHashName} />
                {skin.status === 'hold' && (
                  <HoldIndicator title={t('skins.statusHold')}>
                    ❄️
                  </HoldIndicator>
                )}
                <SkinInfo>
                  <SkinName $error={skin.status === 'error'}>{skin.marketHashName}</SkinName>
                  <SkinPrice>
                    <CurrencyIcon size={14} />
                    {skin.tokenPrice || 0}
                  </SkinPrice>
                </SkinInfo>
              </SkinCard>
            ))}
          </SkinsGrid>
        )}
      </Content>
    </Container>
  );
};

export default MySkins; 